import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { 
  ArrowLeft,
  Search,
  Filter,
  BookOpen,
  Video,
  FileText,
  CheckSquare,
  Download,
  Clock,
  Eye,
  ChevronRight,
  Plane,
  Award,
  Users,
  Globe,
  Briefcase,
  Folder,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API_URL = window.location.origin;

// Icon mapping for categories
const CATEGORY_ICONS = {
  'visa-guides': Plane,
  'sop-tips': FileText,
  'interview-prep': Users,
  'scholarships': Award,
  'pre-departure': CheckSquare,
  'country-guides': Globe,
  'test-prep': BookOpen,
  'career': Briefcase
};

// Content type icons
const CONTENT_TYPE_ICONS = {
  article: FileText,
  video: Video,
  pdf: Download,
  checklist: CheckSquare
};

const ResourceLibraryPage = () => {
  const { isDark } = useTheme();
  const { getLogo } = useAssetContext();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [featuredResources, setFeaturedResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filters from URL
  const selectedCategory = searchParams.get('category') || '';
  const selectedType = searchParams.get('type') || '';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch categories
        const catRes = await fetch(`${API_URL}/api/resources/categories`);
        if (catRes.ok) {
          const data = await catRes.json();
          setCategories(data.categories || []);
        }

        // Fetch featured resources
        const featuredRes = await fetch(`${API_URL}/api/resources/featured?limit=4`);
        if (featuredRes.ok) {
          const data = await featuredRes.json();
          setFeaturedResources(data.resources || []);
        }

        // Build query params
        const params = new URLSearchParams();
        if (selectedCategory) params.append('category', selectedCategory);
        if (selectedType) params.append('content_type', selectedType);
        if (searchQuery) params.append('search', searchQuery);
        params.append('limit', '20');

        // Fetch resources
        const resourceRes = await fetch(`${API_URL}/api/resources?${params}`);
        if (resourceRes.ok) {
          const data = await resourceRes.json();
          setResources(data.resources || []);
        }
      } catch (error) {
        console.error('Error fetching resources:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedCategory, selectedType, searchQuery]);

  const handleCategorySelect = (categoryId) => {
    if (selectedCategory === categoryId) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    setSearchParams(searchParams);
  };

  const handleTypeSelect = (type) => {
    if (selectedType === type) {
      searchParams.delete('type');
    } else {
      searchParams.set('type', type);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchQuery('');
  };

  const getContentTypeIcon = (type) => {
    const Icon = CONTENT_TYPE_ICONS[type] || FileText;
    return Icon;
  };

  const getCategoryIcon = (categoryId) => {
    const Icon = CATEGORY_ICONS[categoryId] || Folder;
    return Icon;
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/uac-desk/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-ux-cyan to-ux-blue flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className={`font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Resource Library</h1>
                  <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Guides & Articles</p>
                </div>
              </div>
            </div>
            <Link to="/">
              <img src={getLogo()} alt="UniXperts" className={`h-8 w-auto ${isDark ? 'brightness-0 invert' : ''}`} />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
          <Input
            placeholder="Search articles, videos, guides..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-12 h-12 text-base ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Content Type Filter */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Type:</span>
          <Tabs value={selectedType || 'all'}>
            <TabsList className="h-9">
              <TabsTrigger 
                value="all" 
                onClick={() => handleTypeSelect('')}
                className="text-xs px-3"
              >
                All
              </TabsTrigger>
              <TabsTrigger 
                value="article" 
                onClick={() => handleTypeSelect('article')}
                className="text-xs px-3 flex items-center gap-1"
              >
                <FileText className="w-3 h-3" />
                Articles
              </TabsTrigger>
              <TabsTrigger 
                value="video" 
                onClick={() => handleTypeSelect('video')}
                className="text-xs px-3 flex items-center gap-1"
              >
                <Video className="w-3 h-3" />
                Videos
              </TabsTrigger>
              <TabsTrigger 
                value="pdf" 
                onClick={() => handleTypeSelect('pdf')}
                className="text-xs px-3 flex items-center gap-1"
              >
                <Download className="w-3 h-3" />
                Downloads
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {(selectedCategory || selectedType || searchQuery) && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
              Clear filters
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className={`rounded-2xl p-4 sticky top-24 ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
              <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Categories
              </h3>
              <div className="space-y-1">
                {categories.map((cat) => {
                  const Icon = getCategoryIcon(cat.id);
                  const isActive = selectedCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                        isActive
                          ? 'bg-ux-cyan/10 text-ux-cyan'
                          : isDark 
                            ? 'text-white/70 hover:bg-white/5 hover:text-white' 
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm flex-1">{cat.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {cat.resource_count || 0}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Resources Grid */}
          <div className="lg:col-span-3">
            {/* Featured Section */}
            {!selectedCategory && !searchQuery && featuredResources.length > 0 && (
              <section className="mb-8">
                <h2 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  Featured Resources
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {featuredResources.map((resource) => {
                    const TypeIcon = getContentTypeIcon(resource.content_type);
                    return (
                      <Link 
                        key={resource.resource_id}
                        to={`/uac-desk/resources/${resource.slug || resource.resource_id}`}
                      >
                        <Card className={`h-full hover:shadow-lg transition-all group ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
                          {resource.thumbnail_url && (
                            <div className="aspect-video overflow-hidden rounded-t-lg">
                              <img 
                                src={resource.thumbnail_url} 
                                alt={resource.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              />
                            </div>
                          )}
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-ux-violet/10 text-ux-violet text-xs">
                                Featured
                              </Badge>
                              <Badge variant="outline" className="text-xs flex items-center gap-1">
                                <TypeIcon className="w-3 h-3" />
                                {resource.content_type}
                              </Badge>
                            </div>
                            <h3 className={`font-semibold line-clamp-2 group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                              {resource.title}
                            </h3>
                            <p className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                              {resource.description}
                            </p>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* All Resources */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  {selectedCategory 
                    ? categories.find(c => c.id === selectedCategory)?.name || 'Resources'
                    : searchQuery 
                      ? `Results for "${searchQuery}"`
                      : 'All Resources'}
                </h2>
                <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {resources.length} resources
                </span>
              </div>

              {loading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className={`rounded-xl p-4 animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-3" />
                      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-full" />
                    </div>
                  ))}
                </div>
              ) : resources.length === 0 ? (
                <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <BookOpen className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
                  <p className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-600'}`}>
                    No resources found
                  </p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {searchQuery ? 'Try a different search term' : 'Check back soon for new content'}
                  </p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {resources.map((resource) => {
                    const TypeIcon = getContentTypeIcon(resource.content_type);
                    return (
                      <Link 
                        key={resource.resource_id}
                        to={`/uac-desk/resources/${resource.slug || resource.resource_id}`}
                      >
                        <Card className={`h-full hover:shadow-md transition-all group ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : ''}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                resource.content_type === 'video' 
                                  ? 'bg-rose-100 text-rose-600' 
                                  : resource.content_type === 'pdf'
                                    ? 'bg-emerald-100 text-emerald-600'
                                    : 'bg-blue-100 text-blue-600'
                              }`}>
                                <TypeIcon className="w-4 h-4" />
                              </div>
                              <Badge variant="outline" className="text-xs capitalize">
                                {resource.content_type}
                              </Badge>
                              {resource.country && (
                                <Badge variant="secondary" className="text-xs">
                                  {resource.country}
                                </Badge>
                              )}
                            </div>
                            
                            <h3 className={`font-semibold line-clamp-2 mb-2 group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                              {resource.title}
                            </h3>
                            
                            <p className={`text-sm line-clamp-2 mb-3 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                              {resource.description}
                            </p>
                            
                            <div className={`flex items-center gap-4 text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                              {resource.reading_time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {resource.reading_time} min
                                </span>
                              )}
                              {resource.views > 0 && (
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  {resource.views}
                                </span>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResourceLibraryPage;
